import { useMemo } from 'react';
import { formatUnixDate } from '@/shared/lib/datetime';
import { useRouteId } from '@/shared/lib/useRouteId';
import { InfoList } from '@/shared/ui/InfoList';
import { FormModalButton } from '@/shared/ui/list/EntityFormModal';
import { GenericListPage } from '@/shared/ui/list/GenericListPage';
import type { ListPageConfig } from '@/shared/ui/list/types';
import { fetchPartner, savePartnerUser, type Partner, type PartnerUser } from './api';

const LIST_KEY = 'marketing-handbook-partner';
const EMPTY = '--';

const userFields = (isEdit: boolean) => [
  { name: 'name', label: 'Имя', placeholder: 'Заголовок...' },
  { name: 'password', label: 'Пароль', placeholder: 'Пароль...' },
  ...(isEdit ? [{ name: 'isActive', label: 'Статус', type: 'checkbox' as const, placeholder: 'Активен' }] : []),
];

const PartnerSummary = ({ partner }: { partner: Partner }) => (
  <div className="row">
    <div className="col-sm-4">
      <InfoList
        title="Партнер"
        items={[
          ['id', partner.id || EMPTY],
          ['name', partner.name || EMPTY],
          ['lat_name', partner.name_lat || EMPTY],
          ['external_id', partner.external_id || EMPTY],
          ['create time', formatUnixDate(partner.create_time) || EMPTY],
        ]}
      />
    </div>
  </div>
);

const createConfig = (partnerId: number): ListPageConfig<PartnerUser> => ({
  queryKey: LIST_KEY,
  title: 'Пользователи',
  errorText: 'Ошибка загрузки партнера',
  fetchList: () => fetchPartner(partnerId),
  rows: (data) => ((data.partner as Partner | undefined)?.user_list ?? []) as PartnerUser[],
  paginated: false,
  headerActions: (
    <FormModalButton
      buttonLabel="Создать"
      buttonClassName="btn btn-info pull-right"
      title="Создать пользователя"
      listKey={LIST_KEY}
      fields={userFields(false)}
      initial={{ name: '', password: '' }}
      save={(v) => savePartnerUser(partnerId, { name: String(v.name), password: String(v.password), isActive: true })}
    />
  ),
  summary: (data) => <PartnerSummary partner={(data.partner as Partner | undefined) ?? {}} />,
  columns: [
    { header: 'id', render: (user) => user.id },
    { header: 'Имя', render: (user) => user.name },
    {
      header: 'Обновить',
      render: (user) => (
        <FormModalButton
          buttonLabel="Обновить"
          title="Обновить пользователя"
          listKey={LIST_KEY}
          fields={userFields(true)}
          initial={{
            name: user.name ?? '',
            password: user.password ?? '',
            isActive: Boolean(Number(user.status_id)),
          }}
          save={(v) =>
            savePartnerUser(partnerId, {
              id: user.id,
              name: String(v.name),
              password: String(v.password),
              isActive: Boolean(v.isActive),
            })
          }
        />
      ),
    },
  ],
});

export const HandbookPartnerPage = () => {
  const partnerId = useRouteId();
  const config = useMemo(() => createConfig(partnerId), [partnerId]);
  return <GenericListPage config={config} />;
};
